import { filterMatchMode, searchMatchMode } from '../../constants/enum';
import { FilterMatchMode, MasterFilterKey, MasterSearchKey, MatchMode } from '../../types/master';

/**
 * 検索キー・絞り込みキーの正規化ユーティリティ（#3180）。
 *
 * マッピングは以下の2形式が混在する。読み取り側は必ずここを通して正規化する。
 *   - 新形式: search_key（オブジェクト）／ filter_keys（配列）を持つ
 *   - 旧形式: search_item_id / search_item_label / search_csv_column / match_mode を持つ
 *
 * データ移行は行わない方針のため、旧形式は読み取り時に新形式へ読み替える。
 */

/** 正規化の入力。MasterMappingType / MasterWithMappingType の両方を受けられる最小形。 */
export interface MatchKeySource {
  search_item_id?: string;
  search_item_label?: string;
  search_csv_column?: string;
  match_mode?: MatchMode;
  search_key?: MasterSearchKey;
  filter_keys?: MasterFilterKey[];
}

/**
 * マッピングから検索キーを得る。
 * search_key が無い旧データは、既存の単一キーフィールドから組み立てる。
 * どちらも無い場合は空の検索キーを返す（呼び出し側で未設定として扱える）。
 */
export const resolveSearchKey = (mapping: MatchKeySource): MasterSearchKey => {
  const searchKey = mapping.search_key;
  if (searchKey && searchKey.item_id) {
    return {
      item_id: searchKey.item_id,
      item_label: searchKey.item_label ?? '',
      csv_column: searchKey.csv_column ?? '',
      // 旧データ由来で match_mode が欠けている場合に備え、'ai' へフォールバックする（#3022 と同じ扱い）。
      match_mode: searchKey.match_mode ?? searchMatchMode.ai,
    };
  }

  return {
    item_id: mapping.search_item_id ?? '',
    item_label: mapping.search_item_label ?? '',
    csv_column: mapping.search_csv_column ?? '',
    match_mode: mapping.match_mode ?? searchMatchMode.ai,
  };
};

/**
 * マッピングから絞り込みキー配列を得る。
 * filter_keys が無い旧データは空配列を返す（＝絞り込みなし＝従来どおりの単一キー照合）。
 *
 * item_id / csv_column のいずれかが欠けた要素は、絞り込み条件として成立しないため除外する。
 * （保存時のバリデーションで防いでいるが、手でデータを入れられた場合の防御）
 */
export const resolveFilterKeys = (mapping: MatchKeySource): MasterFilterKey[] => {
  const filterKeys = mapping.filter_keys;
  if (!Array.isArray(filterKeys)) return [];

  return filterKeys
    .filter(key => !!key && !!key.item_id && !!key.csv_column)
    .map(key => ({
      item_id: key.item_id,
      item_label: key.item_label ?? '',
      csv_column: key.csv_column,
      filter_match_mode: normalizeFilterMatchMode(key.filter_match_mode),
    }));
};

/**
 * 絞り込み方式を正規化する。未知の値・未設定は既定の完全一致に倒す。
 * 絞り込み用途の値はIDコードであることが多く、部分一致より完全一致のほうが誤照合しにくいため。
 */
const normalizeFilterMatchMode = (mode: FilterMatchMode | undefined): FilterMatchMode =>
  mode === filterMatchMode.keyword ? filterMatchMode.keyword : filterMatchMode.exact;

/**
 * 絞り込みキーが設定されているか。
 * 一覧表示で「絞り込み」セクションを出すかの判定に使う。
 */
export const hasFilterKeys = (mapping: MatchKeySource): boolean =>
  resolveFilterKeys(mapping).length > 0;

/**
 * 検索キーから、後方互換のために書き続ける既存フィールド（ミラー）を組み立てる。
 * search_key が正で、既存フィールドはその写し。保存時に必ず両方を書く。
 */
export const buildLegacyMirror = (
  searchKey: MasterSearchKey,
): {
  searchItemId: string;
  searchItemLabel: string;
  searchCsvColumn: string;
  matchMode: MatchMode;
} => ({
  searchItemId: searchKey.item_id,
  searchItemLabel: searchKey.item_label,
  searchCsvColumn: searchKey.csv_column,
  matchMode: searchKey.match_mode,
});
